import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUserSecret, faTimesCircle, faUnlockAlt } from "@fortawesome/free-solid-svg-icons"
import { recheckWhileViewingAs, stopViewAs } from "../../../domain/user"
import { applyViewAsSession } from "../../../utils/viewAsSession"
import { viewAsKindOf } from "../../../utils/viewAs"
import { VIEW_AS, recheckErrorFor } from "../../../consts/viewAs"
import "./index.scss"

/**
 * "Viewing as <name>" -- shown on EVERY screen while an admin is viewing the
 * app as another account, admin screens and ordinary screens alike
 * (../wicked-maths-laravel/docs/VIEW_AS_USER.md). That account may be a child, an
 * adult student or a parent, so nothing here says "student" unless the server
 * said so: the consequence line names the kind when `viewing_as.user_kind`
 * arrives, and falls back to the role-neutral sentence when it does not.
 *
 * It is mounted next to the other app-wide banners in components/index.js
 * rather than inside a page, because the dangerous case is precisely the one
 * where the admin has wandered off the admin area and forgotten: from that
 * moment every GOT IT they click lands on a real person's record. So it is
 * deliberately hard to miss and cannot be dismissed -- the only way to remove it
 * is to stop.
 *
 * It renders nothing for an ordinary session. `viewing_as` is refreshed from the
 * server on every /api/me, so it is the API that decides an impersonation is
 * live, not something remembered in localStorage.
 *
 * ## The second button: "Check what this account can open now"
 *
 * It is here, on the banner, and not on an admin screen, because of a single
 * fact about View As: the SPA holds ONE bearer token while a session is live --
 * the account's -- so every /api/admin/* call answers 403. That is precisely why
 * POST /api/view-as/recheck exists outside the admin group and gates itself on
 * the View As token instead (../wicked-maths-laravel/routes/api.php). No token
 * juggling is needed: the global Authorization header is already the right one.
 *
 * It matters because View As DELIBERATELY suppresses the automatic unlock, so
 * that merely looking at a child's course cannot move their progression. The
 * consequence is that a View As session shows MORE locked than reality, and this
 * is how she resolves that without leaving the account's view. See
 * ../wicked-maths-laravel/docs/SCHEDULED_WORK.md §10.
 *
 * The wording -- label, reassurance, result and every error -- lives in
 * consts/viewAs.js. Nothing user-facing is written in this file.
 */
const ViewAsBanner = ({ user, win = window }) => {
	const viewingAs = user ? user.viewing_as : null
	const [stopping, setStopping] = useState(false)
	const [failed, setFailed] = useState(false)

	/*
	 * The re-check's own state, kept apart from `stopping`/`failed` on purpose:
	 * the two controls fail for different reasons and a failed re-check must not
	 * make the way OUT of the session look broken. `checkResult` is a one-line
	 * answer, positive or negative, shown until the next press.
	 */
	const [checking, setChecking] = useState(false)
	const [checkResult, setCheckResult] = useState(null)

	// Pushes the page down so a fixed bar never sits on top of the header.
	// Mirrors the `pwa-standalone` body-class idiom in components/index.js.
	useEffect(() => {
		if (!viewingAs) return undefined

		document.body.classList.add("is-viewing-as")

		return () => document.body.classList.remove("is-viewing-as")
	}, [viewingAs])

	if (!viewingAs) return null

	const name = [user.first_name, user.last_name].filter(Boolean).join(" ")

	const stop = async () => {
		if (stopping) return

		setStopping(true)
		setFailed(false)

		try {
			const res = await stopViewAs()

			// Back to the admin area, where the View As control was pressed.
			applyViewAsSession(
				{ token: res.data.data.token, user: { ...res.data.data.user, viewing_as: null } },
				win,
				"/admin",
			)
		} catch (error) {
			console.error("Error stopping View As:", error)
			setFailed(true)
			setStopping(false)
		}
	}

	/*
	 * Evaluate the automatic rules again for the account being viewed.
	 *
	 * The `checking` guard is the same shape as `stopping` above, and it is not
	 * belt-and-braces: this can take a few seconds, because it evaluates EVERY
	 * course the account is on under a statement ceiling, so a second click
	 * lands well inside the first request. Two presses would only cost a wasted
	 * round trip -- the endpoint is idempotent -- but the second answer would
	 * overwrite the first on screen, and a "nothing new to open" arriving after
	 * an "opened 2 new areas" reads as though the first was wrong.
	 */
	const recheck = async () => {
		if (checking) return

		setChecking(true)
		setCheckResult(null)

		try {
			const res = await recheckWhileViewingAs()
			/*
			 * `areas_opened` is a number from the server. Read defensively and
			 * coerced, because this file renders it: a missing or non-numeric
			 * value would otherwise reach opened() and come back as "Opened
			 * undefined new areas", and an object would throw during render,
			 * outside this catch, where React 18 unmounts the whole root (the
			 * failure PR #109 documents). Treating an unreadable answer as 0 is
			 * safe here BECAUSE zero is good news rather than an error: the worst
			 * case is that she is told nothing opened and presses again, which
			 * costs nothing.
			 */
			const body = (res && res.data && res.data.data) || {}
			const opened = Number.isFinite(Number(body.areas_opened)) ? Number(body.areas_opened) : 0

			setCheckResult({ ok: true, text: VIEW_AS.recheck.opened(opened) })
		} catch (error) {
			// Our own sentence, keyed on the status. Never error.response.data.message
			// -- see the note on VIEW_AS.recheck.errors.
			setCheckResult({ ok: false, text: recheckErrorFor(error) })
		}

		setChecking(false)
	}

	return (
		<div className="ViewAsBanner" role="status">
			<div className="banner-content">
				<FontAwesomeIcon icon={faUserSecret} className="view-as-icon" />
				<div className="text">
					<strong>{VIEW_AS.banner.viewing(name)}</strong>
					<span>{failed ? VIEW_AS.banner.failed : VIEW_AS.banner.consequence(viewAsKindOf(viewingAs))}</span>
				</div>
			</div>
			<div className="banner-actions">
				<div className="action-buttons">
					{/*
					 * Before Stop, because it is the thing she came to do; Stop is the
					 * way out and stays at the end where it has always been, so its
					 * position never moves under her.
					 */}
					<button type="button" className="recheck-btn" onClick={recheck} disabled={checking}>
						<FontAwesomeIcon icon={faUnlockAlt} />{" "}
						{checking ? VIEW_AS.recheck.running : VIEW_AS.recheck.label}
					</button>
					<button type="button" className="stop-btn" onClick={stop} disabled={stopping}>
						<FontAwesomeIcon icon={faTimesCircle} />{" "}
						{stopping ? VIEW_AS.banner.stopping : VIEW_AS.banner.stop}
					</button>
				</div>
				{/*
				 * The answer, and the standing reassurance when there is no answer
				 * yet. It sits UNDER THE BUTTON it describes.
				 *
				 * It used to sit in the banner's text column on the left, and the
				 * reasoning recorded here was "so it is read where she is already
				 * looking rather than in a toast she has to connect back to a
				 * button". That argument was against a TOAST and it still holds; it
				 * was never an argument for putting the sentence at the opposite end
				 * of a full-width bar from the control it belongs to. Over there it
				 * read as a third line of "Everything you do is recorded against
				 * this student's account", which is a warning about the session, so
				 * a reassurance about a button looked like another caution about
				 * impersonation. Reported on staging, 26 Sep 2026: "it's on the far
				 * left side and looks a little confusing".
				 *
				 * `role="status"` on the banner already announces changes here, and
				 * aria-live is set explicitly because this text replaces itself in
				 * place: a result arriving seconds after the press is the whole
				 * point and must not be silent to a screen reader.
				 *
				 * A failed re-check is marked by a class and by its WORDS, never by
				 * colour alone -- the same rule the rest of this banner keeps.
				 */}
				<span
					className={checkResult && !checkResult.ok ? "recheck-line recheck-line-failed" : "recheck-line"}
					aria-live="polite"
				>
					{checkResult ? checkResult.text : VIEW_AS.recheck.reassurance}
				</span>
			</div>
		</div>
	)
}

export default connect(state => ({
	user: state.authReducer.data.user,
}))(ViewAsBanner)
